<script lang="ts" setup>
import type { ProductListItem } from "~~/models/products";
import type { ProductSearchDto, PropertyFilterDto } from "~~/server/api/ecom/products/search.post";
import type { HappyDealsSectionRecord } from "~~/server/graphql/datocms/generated";
import type { PlanktonProductSearchIdResponse, PlanktonProductSearchResponse } from "~~/server/plankton";
import { PlanktonSearchResultSortOrder } from "~~/server/plankton";

const { data } = defineProps<{ data: HappyDealsSectionRecord }>();

const { locale } = useI18n();
const localePath = useLocalePath();
const { loggedIn } = useUserSession();

const cmsData = await useCatalogPage();
const layout = await useLayout();

const propertyValueFilterList = Array.isArray(cmsData?.value?.happyDealsPropertyFilter)
  ? [JSON.parse(cmsData?.value?.happyDealsPropertyFilter.at(0).value) as PropertyFilterDto]
  : undefined;

const { data: happyDeals } = await useAsyncData("happy-deals", async () => {
  const response = await $fetch("/api/ecom/products/search", {
    method: "POST",
    body: {
      propertyValueFilterList: propertyValueFilterList,
      sortOrder: PlanktonSearchResultSortOrder.PersonalizedThenProductNumberAscending, // = ABC-sortering, "Standaard"/"Défaut" in ML labels
      pageSize: 4,
    } as ProductSearchDto,
  });

  const responseData = response as { isFallback: boolean; results?: PlanktonProductSearchResponse | undefined };

  const productIdList = responseData?.results?.pagedProducts[1]
    ?.sort((a: PlanktonProductSearchIdResponse, b: PlanktonProductSearchIdResponse) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((p: PlanktonProductSearchIdResponse) => p.productId) ?? [];

  return await $fetch<Array<ProductListItem>>("/api/ecom/products/list", {
    query: {
      productIdList: productIdList,
      addCategories: true,
      addExternalData: true,
      addFiles: true,
      addOverpacks: true,
      addOverpackTranslations: true,
      addProductTranslations: true,
      addPropertyValues: true,
      locale: locale.value, // Use the current locale (the detector would fallback to the default locale at SSR)
    },
  });
});

// only load prices when authenticated
const { prices, isPricesLoading } = usePricing(happyDeals, { loggedIn: loggedIn.value });
</script>

<template>
  <div v-if="happyDeals?.length && cmsData && layout" class="mb-3xl lg:mb-5xl">
    <CenterCard view-mode="wide" class="gap-xl flex flex-col">
      <h3 class="text-primary-500 gap-xs flex items-center justify-center">
        <img src="/icons/promo.svg" alt="Happy Deals" class="size-[64px]">
        {{ data.title }}
      </h3>

      <div class="gap-md grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <div v-for="product of happyDeals" :key="product.id">
          <ProductCardItem
            :layout="layout"
            :cms-data="cmsData!"
            :product="product"
            :prices="prices"
            :is-prices-loading="isPricesLoading"
            :overpack-popper="{ placement: 'bottom' }"
          />
        </div>
      </div>

      <div class="flex justify-center">
        <UButton size="xl" :ui="{ base: 'leading-7' }" :to="localePath('/happy-deals')">
          {{ data.buttonText }}
          <img src="/icons/promo-revert.svg" alt="" class="size-[24px]">
        </UButton>
      </div>
    </CenterCard>
  </div>
</template>

<style scoped>

</style>
